// src/components/Admin/AdminPlatformDefaults.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase'; // Adjust this path if your firebase.js is located elsewhere
import { toast } from 'react-toastify';

const AdminPlatformDefaults = () => {
  // State for dropdown data
  const [agreements, setAgreements] = useState([]);
  const [templates, setTemplates] = useState([]);

  // State for user selections
  const [selectedAgreementId, setSelectedAgreementId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // State for UI feedback
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [claudeOpusFrequency, setClaudeOpusFrequency] = useState(0);

  useEffect(() => {
    setLoading(true);
    const defaultsRef = doc(db, 'settings', 'platformDefaults');

    // Set up the real-time listener
    const unsubscribe = onSnapshot(defaultsRef, (docSnap) => {
      if (docSnap.exists()) {
        const defaultsData = docSnap.data();
        setSelectedAgreementId(defaultsData.defaultAgreementId || '');
        setSelectedTemplateId(defaultsData.defaultQuestionnaireTemplateId || '');
        setClaudeOpusFrequency(defaultsData.globalClaudeOpusFrequency ?? 0);
      } else {
        // Handle case where document doesn't exist yet
        console.log("Platform config document does not exist.");
      }
      setLoading(false);
    }, (err) => {
      console.error("Error listening to platform defaults:", err);
      setError('Failed to load real-time settings.');
      toast.error('Failed to load real-time settings.');
      setLoading(false);
    });

    // Fetch the static lists of agreements and templates once
    const fetchLists = async () => {
        try {
            const agreementsQuery = query(collection(db, 'userAgreements'), where('active', '==', true));
            const agreementsSnapshot = await getDocs(agreementsQuery);
            setAgreements(agreementsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const templatesQuery = query(collection(db, 'questionnaireTemplates'), where('status', '==', 'Active'));
            const templatesSnapshot = await getDocs(templatesQuery);
            setTemplates(templatesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error("Error fetching lists:", err);
            toast.error("Failed to load dropdown lists.");
        }
    };

    fetchLists();

    // Cleanup function to unsubscribe when the component unmounts
    return () => unsubscribe();
  }, []); // Empty dependency array ensures this runs only once on mount

  const handleSaveChanges = async () => {
    if (!selectedAgreementId || !selectedTemplateId) {
      toast.error('Please select a default for both fields.');
      return;
    }
    setSaving(true);
    try {
      const defaultsRef = doc(db, 'settings', 'platformDefaults');
      await setDoc(defaultsRef, {
      defaultAgreementId: selectedAgreementId,
      defaultQuestionnaireTemplateId: selectedTemplateId,
    }, { merge: true }); // NOTE: globalClaudeOpusFrequency is removed

      toast.success('Platform defaults have been updated!');
    } catch (err) {
      console.error("Error saving platform defaults:", err);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography>Loading default settings...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
  Platform Default Settings
</Typography>
<Typography variant="body2" color="text.secondary" paragraph>
  Configure default settings for new users who sign up automatically from the marketing website.
</Typography>
<Typography variant="body2" color="text.secondary">
  Current Claude Opus Frequency: {claudeOpusFrequency} (0=never, 1=always, 3=every 3rd question)
</Typography>

<Box sx={{ maxWidth: 500, mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="default-agreement-label">Default User Agreement</InputLabel>
          <Select
            labelId="default-agreement-label"
            value={selectedAgreementId}
            label="Default User Agreement"
            onChange={(e) => setSelectedAgreementId(e.target.value)}
          >
            {agreements.map((agreement) => (
              <MenuItem key={agreement.id} value={agreement.id}>
                {agreement.title} (v{agreement.version})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>This agreement will be presented to new self-service users.</FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="default-template-label">Default Questionnaire Template</InputLabel>
          <Select
            labelId="default-template-label"
            value={selectedTemplateId}
            label="Default Questionnaire Template"
            onChange={(e) => setSelectedTemplateId(e.target.value)}
          >
            {templates.map((template) => (
              <MenuItem key={template.id} value={template.id}>
                {template.name} (v{template.version})
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>This questionnaire will be assigned to new self-service users.</FormHelperText>
        </FormControl>

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={saving}
            sx={{ position: 'relative' }}
          >
            Save Changes
            {saving && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminPlatformDefaults;